import React, { useState } from 'react';
import { FaTimes, FaEye, FaDownload, FaDollarSign, FaGift, FaSpinner, FaCopy, FaMapMarkerAlt, FaServer, FaExclamationTriangle } from 'react-icons/fa';

// Tiny copy-to-clipboard chip. Renders the value monospace next to a copy icon;
// admin uses this to grab AVC IDs, LOC codes, ServiceIDs for the Vocus portal
// without highlighting + cmd-C every time.
const CopyChip = ({ value, label, mono = true }) => {
    const [copied, setCopied] = useState(false);
    if (!value) return <span className="text-[#928374] italic">not captured</span>;
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch { /* ignore — fallback browsers */ }
    };
    return (
        <button
            onClick={copy}
            title={`Copy ${label || 'value'}`}
            className={`inline-flex items-center gap-2 px-2 py-1 rounded bg-[#1d2021] border border-[#504945] hover:border-[#fabd2f] transition-colors group ${mono ? 'font-mono' : ''}`}
        >
            <span className="text-[#ebdbb2] text-sm">{value}</span>
            {copied
                ? <span className="text-xs text-[#b8bb26]">✓ copied</span>
                : <FaCopy size={11} className="text-[#928374] group-hover:text-[#fabd2f]" />}
        </button>
    );
};
import PricingOverrideForm from './PricingOverrideForm';
import ConsentsSection from './ConsentsSection';
import CustomerInsightsSection from './CustomerInsightsSection';
import ProvisioningSection from './ProvisioningSection';
import CpeSection from './CpeSection';
import { useCustomerPricing } from '../../hooks/useCustomerPricing';
import { resolveActiveOverride } from '../../utils/effectivePricing';

const CANCEL_ENDPOINT = 'https://api.peaknet.com.au/cancel';

const CustomerDetailsModal = ({
    customer,
    onClose,
    updateLocalCustomer, // Function to update customer in parent list
    onViewDocument,
    onDownloadDocument,
    onOpenActionHistory,
    onResetPassword,
    onApprove,
    onReject
}) => {
    // Use the extracted hook for pricing logic
    const pricing = useCustomerPricing(customer, updateLocalCustomer);

    // Manual admin adjustments — ad-hoc refunds and goodwill promo offers
    // outside the cancel flow. Hits customer-cancel-request Lambda's
    // adminIssueRefund + adminApplyPromo actions.
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [showPromoModal, setShowPromoModal] = useState(false);

    // Helper for displaying price
    const formatDisplayDate = (value) => {
        if (!value) return 'Not set';
        try {
            return new Date(value).toLocaleDateString();
        } catch { return value; }
    };

    const effectivePrice = (() => {
        const p = pricing.customerPricingDetails?.pricing;
        if (p?.override?.status === 'active' && typeof p.override.amount === 'number') {
            return p.override.amount;
        }
        return p?.baseAmount;
    })();

    const isApproved = customer.status === 'approved';
    const isRejected = customer.status === 'rejected';
    const isCancelled = customer.status === 'cancelled';
    // Show the Approve action whenever the account hasn't been billing-approved yet — not only
    // when status === 'pending'. Covers manually-provisioned customers (status 'active' but
    // billingStatus not yet 'approved'), who otherwise had no way to start billing from here.
    const canApprove =
        customer.status === 'pending' ||
        (customer.status === 'active' && customer.billingStatus !== 'approved');

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#282828] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-[#3c3836]">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <h2 className="text-2xl font-bold text-[#ebdbb2]">Customer Details</h2>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${isApproved ? 'bg-[#b8bb26] text-[#1d2021]' :
                                isRejected ? 'bg-[#fb4934] text-[#1d2021]' :
                                    isCancelled ? 'bg-[#fb4934] text-[#1d2021]' :
                                        'bg-[#fabd2f] text-[#1d2021]'
                                }`}>
                                {customer.status}
                            </span>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-[#928374] hover:text-[#bdae93]"
                        >
                            <FaTimes size={24} />
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {/* Basic Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">Personal Information</h3>
                            <div className="space-y-3">
                                <div>
                                    <label className="text-sm font-medium text-[#928374]">Full Name</label>
                                    <p className="text-[#ebdbb2]">{customer.fullName}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-[#928374]">Email</label>
                                    <div className="flex items-center space-x-2">
                                        <p className="text-[#ebdbb2]">{customer.email}</p>
                                        <button onClick={onResetPassword} className="text-xs bg-[#3c3836] hover:bg-[#504945] px-2 py-1 rounded text-[#fabd2f]">
                                            Reset Password
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-[#928374]">Phone</label>
                                    <p className="text-[#ebdbb2]">{customer.phone}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-[#928374]">AVC ID</label>
                                    <div className="flex items-center space-x-2 mt-1">
                                        <input
                                            type="text"
                                            value={pricing.avcIdInput}
                                            onChange={(e) => pricing.setAvcIdInput(e.target.value)}
                                            placeholder="e.g. AVC000123456"
                                            className="flex-1 px-3 py-1 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] text-sm"
                                        />
                                        <button
                                            onClick={pricing.saveAvcId}
                                            disabled={pricing.avcIdSaving}
                                            className="px-3 py-1 bg-[#fabd2f] text-[#1d2021] rounded text-sm font-medium hover:bg-[#fe8019] disabled:opacity-50"
                                        >
                                            {pricing.avcIdSaving ? '...' : 'Save'}
                                        </button>
                                    </div>
                                    {pricing.avcIdError && <p className="text-xs text-red-500 mt-1">{pricing.avcIdError}</p>}
                                    {pricing.avcIdSuccess && <p className="text-xs text-green-500 mt-1">{pricing.avcIdSuccess}</p>}
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">Service Information</h3>
                            <div className="space-y-3">
                                <div>
                                    <label className="text-sm font-medium text-[#928374]">Plan</label>
                                    <p className="text-[#ebdbb2]">{customer.plan}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-[#928374]">Monthly Fee</label>
                                    <p className="text-[#ebdbb2]">${customer.monthlyFee}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-[#928374]">Account Number</label>
                                    <p className="text-[#ebdbb2]">{customer.accountNumber}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-[#928374]">Service Start Date</label>
                                    <p className="text-[#ebdbb2]">{
                                        customer.serviceStartDate === 'asap'
                                            ? 'ASAP'
                                            : (customer.serviceStartDate || customer.serviceDate)
                                                ? new Date(customer.serviceStartDate || customer.serviceDate).toLocaleDateString('en-AU')
                                                : '—'
                                    }</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-[#928374]">Next Billing Date</label>
                                    <p className="text-[#ebdbb2]">
                                        {formatDisplayDate(customer.nextBillingDate)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Service Address — pulled from customer signup. Read-only here;
                        admin can correct via the customer-portal "Update Address"
                        flow if needed. */}
                    <div className="bg-[#282828] border border-[#3c3836] rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <FaMapMarkerAlt className="text-[#83a598]" />
                            <h3 className="text-lg font-semibold text-[#ebdbb2]">Service Address</h3>
                        </div>
                        {customer.address ? (
                            <p className="text-[#ebdbb2]">{customer.address}</p>
                        ) : customer.street ? (
                            <p className="text-[#ebdbb2]">
                                {[customer.unitType && customer.unitNumber ? `${customer.unitType} ${customer.unitNumber}` : null,
                                  customer.street,
                                  customer.suburb,
                                  customer.state,
                                  customer.postcode].filter(Boolean).join(', ')}
                            </p>
                        ) : (
                            <p className="text-[#928374] italic">No address on file</p>
                        )}
                    </div>

                    {/* Vocus / NBN Service Identifiers — surface everything the admin
                        needs to copy into the Vocus portal or troubleshoot a service.
                        Missing values render as 'not captured' so the gap is visible. */}
                    <div className="bg-[#282828] border border-[#3c3836] rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <FaServer className="text-[#fabd2f]" />
                            <h3 className="text-lg font-semibold text-[#ebdbb2]">Vocus / NBN Identifiers</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                            <div>
                                <label className="text-xs font-medium text-[#928374] uppercase block mb-1">LOC (NBN Location ID)</label>
                                <CopyChip value={customer.vocusSqSnapshot?.nbnLocationId} label="LOC" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#928374] uppercase block mb-1">Access Technology</label>
                                <span className="text-[#ebdbb2] text-sm">{customer.vocusSqSnapshot?.accessTechnology || <span className="text-[#928374] italic">unknown</span>}</span>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#928374] uppercase block mb-1">AVC ID</label>
                                <CopyChip value={customer.avcId} label="AVC ID" />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-[#928374] uppercase block mb-1">Vocus Service ID</label>
                                <CopyChip value={customer.vocusServiceId || customer.vocusOrderSnapshot?.serviceId} label="Service ID" />
                            </div>
                        </div>

                        {/* Inline warning — approved customer missing AVC ID or Service ID
                            means billing will run but cancel/move flows can't action the
                            Vocus side. Catch it before it becomes an ops fire. */}
                        {isApproved && (() => {
                            const missing = [];
                            if (!customer.avcId) missing.push('AVC ID');
                            if (!(customer.vocusServiceId || customer.vocusOrderSnapshot?.serviceId)) missing.push('Vocus Service ID');
                            if (missing.length === 0) return null;
                            return (
                                <div className="mt-4 p-3 bg-[#3c1f1f] border border-[#cc241d] rounded-lg flex items-start gap-2">
                                    <FaExclamationTriangle className="text-[#fb4934] mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-[#fbf1c7]">
                                        <p className="font-medium text-[#fb4934] mb-1">
                                            Approved customer missing: {missing.join(', ')}
                                        </p>
                                        <p className="text-xs">
                                            Billing will still run, but cancel/move actions can't target the Vocus service without these. Capture them now by re-opening the Approve flow or via the AVC ID field above.
                                        </p>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    {/* Customer Insights — LTV, churn risk, payment failures,
                        3-month forecast, promo cost-to-date. Loads its own
                        data from getCustomerInsights backend action. */}
                    <CustomerInsightsSection customer={customer} />

                    {/* Provisioning record — mark auto/manual once the service is provisioned. */}
                    <ProvisioningSection customer={customer} updateLocalCustomer={updateLocalCustomer} />

                    {/* CPE router — TR-069/GenieACS assign + live status/reboot (docs/GENIEACS-SETUP.md) */}
                    <CpeSection customer={customer} updateLocalCustomer={updateLocalCustomer} />

                    {/* Pricing & Overrides Component */}
                    <PricingOverrideForm
                        basePriceInput={pricing.basePriceInput}
                        setBasePriceInput={pricing.setBasePriceInput}
                        baseSaving={pricing.baseSaving}
                        onSaveBasePrice={pricing.saveBasePrice}

                        overrideForm={pricing.overrideForm}
                        handleOverrideInputChange={pricing.handleOverrideInputChange}
                        overrideSaving={pricing.overrideSaving}
                        onSaveOverride={pricing.saveOverride}

                        currentOverride={resolveActiveOverride(pricing.customerPricingDetails)}
                        clearingOverride={pricing.clearingOverride}
                        onClearOverride={pricing.clearOverride}

                        pricingLoading={pricing.pricingLoading}
                        pricingError={pricing.pricingError}
                        pricingSuccess={pricing.pricingSuccess}

                        planPriceDisplay={customer.monthlyFee}
                        basePriceDisplay={pricing.customerPricingDetails?.pricing?.baseAmount ?? customer.monthlyFee}
                        effectivePrice={effectivePrice}
                    />

                    {/* Advanced corrections — Service activation date + Billing schedule.
                        These overlap with the Approval flow (which captures activation date)
                        and the daily recurring billing cron (which sets next billing date),
                        so they're for after-the-fact fixes only. Collapsed behind a disclosure
                        to keep the primary detail view focused. */}
                    <details className="bg-[#282828] border border-[#3c3836] rounded-lg">
                        <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-[#bdae93] hover:bg-[#3c3836] rounded-lg select-none">
                            ⚙ Advanced corrections — Service activation date + Billing schedule
                        </summary>
                        <div className="p-4 border-t border-[#3c3836] space-y-6">
                            {/* Service Activation Date — overrides the date set at approval time. */}
                            <div>
                                <h4 className="text-base font-semibold text-[#ebdbb2] mb-2">Service Activation Date</h4>
                                {pricing.serviceActivationError && <div className="text-sm text-red-600 mb-2">{pricing.serviceActivationError}</div>}
                                {pricing.serviceActivationSuccess && <div className="text-sm text-green-600 mb-2">{pricing.serviceActivationSuccess}</div>}
                                <div className="flex md:items-end flex-col md:flex-row gap-4">
                                    <div className="flex-1">
                                        <input
                                            type="date"
                                            value={pricing.serviceActivationDateInput}
                                            onChange={(e) => pricing.setServiceActivationDateInput(e.target.value)}
                                            className="mt-1 w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2]"
                                        />
                                    </div>
                                    <button
                                        onClick={pricing.saveServiceActivationDate}
                                        disabled={pricing.serviceActivationSaving}
                                        className="px-5 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg font-medium hover:bg-[#fe8019] h-10"
                                    >
                                        {pricing.serviceActivationSaving ? 'Saving...' : 'Save Date'}
                                    </button>
                                </div>
                            </div>

                            {/* Billing Schedule — overrides nextBillingDate. Use only to fix
                                drift between the cron and where you actually want to bill. */}
                            <div>
                                <h4 className="text-base font-semibold text-[#ebdbb2] mb-2">Billing Schedule</h4>
                                {pricing.billingError && <div className="text-sm text-red-600 mb-2">{pricing.billingError}</div>}
                                {pricing.billingSuccess && <div className="text-sm text-green-600 mb-2">{pricing.billingSuccess}</div>}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-[#bdae93]">Next billing date</label>
                                        <input
                                            type="date"
                                            value={pricing.billingDateInput}
                                            onChange={(e) => pricing.setBillingDateInput(e.target.value)}
                                            className="mt-1 w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2]"
                                        />
                                    </div>
                                    <div className="flex items-end">
                                        <button
                                            onClick={pricing.saveBillingDate}
                                            disabled={pricing.billingSaving}
                                            className="px-5 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg font-medium hover:bg-[#fe8019] h-10 w-full md:w-auto"
                                        >
                                            {pricing.billingSaving ? 'Saving...' : 'Save Billing Date'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </details>

                    {/* Verification & Docs */}
                    <div>
                        <h3 className="text-lg font-semibold text-[#ebdbb2] mb-4">Verification</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-sm font-medium text-[#928374]">ID Type</label>
                                <p className="text-[#ebdbb2]">{customer.idType}</p>
                            </div>
                        </div>

                        <div className="mt-6">
                            <label className="text-sm font-medium text-[#928374]">Uploaded Document</label>
                            <div className="mt-2">
                                {customer.uploadedDocument ? (
                                    <div className="flex items-center space-x-3">
                                        <div className="flex-1">
                                            <p className="text-sm text-[#ebdbb2]">
                                                {customer.uploadedDocument.name || 'Document uploaded'}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => onViewDocument(customer)}
                                            className="px-3 py-1 bg-[#fabd2f] text-[#1d2021] text-sm rounded-lg hover:bg-[#fe8019] transition-colors"
                                        >
                                            <FaEye className="mr-1" /> View
                                        </button>
                                        <button
                                            onClick={() => onDownloadDocument(customer)}
                                            className="px-3 py-1 bg-[#b8bb26] text-[#1d2021] text-sm rounded-lg hover:bg-[#98971a] transition-colors"
                                        >
                                            <FaDownload className="mr-1" /> Download
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center space-x-3">
                                        <p className="text-sm text-[#928374]">No document uploaded</p>
                                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Pending</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Action History Button */}
                    {onOpenActionHistory && (
                        <div className="pt-6 border-t border-[#3c3836]">
                            <button
                                onClick={onOpenActionHistory}
                                className="text-[#fabd2f] hover:text-[#fe8019] text-sm font-medium"
                            >
                                View Action History Protocol
                            </button>
                        </div>
                    )}

                    {/* Consents & Agreements audit trail — legal-compliance evidence per
                        TCP Code §3.2. Lists every consent recorded at signup or admin-recorded
                        post-signup, with timestamp + IP + user agent + document version. */}
                    <ConsentsSection customer={customer} />

                    {/* Account Actions — only visible while the customer is in a
                        state where Approve/Reject still make sense. Once approved,
                        rejected or cancelled, these buttons just clutter the page
                        and the previously-shown "Retry Initial Charge" footgun
                        moved to the Billing tab (per-invoice retry is the right
                        surface for it). */}
                    {canApprove && (
                        <div className="pt-6 border-t border-[#3c3836] mt-6">
                            <h3 className="text-[#ebdbb2] font-semibold mb-3">Account Actions</h3>
                            {customer.status === 'active' && (
                                <p className="text-sm text-[#928374] mb-3">
                                    This account is provisioned but billing hasn&apos;t started yet. Approve to begin billing
                                    (charges the first pro-rated invoice and enrolls them in recurring billing).
                                </p>
                            )}
                            <div className="flex flex-wrap gap-4">
                                <button
                                    onClick={onApprove}
                                    className="px-6 py-2 rounded-lg font-medium transition-colors bg-[#b8bb26] text-[#1d2021] hover:bg-[#98971a]"
                                >
                                    {customer.status === 'pending' ? 'Approve Account' : 'Approve & Start Billing'}
                                </button>
                                {customer.status === 'pending' && (
                                    <button
                                        onClick={onReject}
                                        className="px-6 py-2 rounded-lg font-medium transition-colors bg-transparent border border-[#fb4934] text-[#fb4934] hover:bg-[#fb4934] hover:text-[#1d2021]"
                                    >
                                        Reject Account
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Status note when no actions available — keeps admin oriented. */}
                    {!canApprove && (
                        <div className="pt-6 border-t border-[#3c3836] mt-6">
                            <p className="text-sm text-[#928374]">
                                Status: <span className="text-[#fabd2f] font-medium uppercase">{customer.status}</span>
                                {isApproved && ' — to re-charge a failed initial invoice, open the Billing Management tab and retry the invoice directly.'}
                                {isRejected && ' — this account was rejected and cannot accept new actions from this page.'}
                                {isCancelled && ' — this account is cancelled. The email is freed for re-signup.'}
                            </p>
                        </div>
                    )}

                    {/* Manual admin adjustments — refund + promo (always visible
                        unless the customer is already cancelled). These act
                        independently of the cancel-request flow. */}
                    {!isCancelled && (
                        <div className="pt-6 border-t border-[#3c3836] mt-6">
                            <h3 className="text-[#fabd2f] font-semibold mb-2">Manual Adjustments</h3>
                            <p className="text-sm text-[#928374] mb-3">
                                Issue a goodwill refund to the customer's last paid invoice, or attach a retention/goodwill promo to their account.
                            </p>
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    onClick={() => setShowRefundModal(true)}
                                    className="px-4 py-2 border border-[#fabd2f] text-[#fabd2f] rounded hover:bg-[#fabd2f] hover:text-[#1d2021] transition-colors inline-flex items-center gap-2 text-sm"
                                >
                                    <FaDollarSign /> Issue Refund / Credit Note
                                </button>
                                <button
                                    onClick={() => setShowPromoModal(true)}
                                    className="px-4 py-2 border border-[#83a598] text-[#83a598] rounded hover:bg-[#83a598] hover:text-[#1d2021] transition-colors inline-flex items-center gap-2 text-sm"
                                >
                                    <FaGift /> Apply Promo / Offer
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Cancellation pointer — the old inline "Cancel Service" button
                        was a half-implemented quick-flip that left orphaned data
                        (Cognito user not deleted, invoices not renamed, Vocus service
                        still active, no retention offer). The proper Cancel Service
                        feature lives under the "Cancel Requests" tab and handles all
                        of that. Direct admin there instead of duplicating the button. */}
                    {!isCancelled && (
                        <div className="pt-6 border-t border-[#3c3836] mt-6">
                            <h3 className="text-[#928374] font-semibold mb-2">Cancellation</h3>
                            <p className="text-sm text-[#928374]">
                                To cancel this customer's service, use the <span className="text-[#fabd2f] font-medium">Cancel Requests</span> tab.
                                The full flow there handles email release, Cognito cleanup, Vocus deactivation,
                                invoice rename, and the retention-offer workflow — none of which the old
                                inline button did.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {showRefundModal && (
                <ManualRefundModal
                    customer={customer}
                    onClose={() => setShowRefundModal(false)}
                    onSuccess={() => {
                        setShowRefundModal(false);
                        // Trigger parent refresh if available
                        if (updateLocalCustomer) updateLocalCustomer(customer);
                    }}
                />
            )}
            {showPromoModal && (
                <ManualPromoModal
                    customer={customer}
                    onClose={() => setShowPromoModal(false)}
                    onSuccess={() => {
                        setShowPromoModal(false);
                        if (updateLocalCustomer) updateLocalCustomer(customer);
                    }}
                />
            )}
        </div>
    );
};

// ───────── Manual Refund Modal ─────────
const ManualRefundModal = ({ customer, onClose, onSuccess }) => {
    const [amount, setAmount] = useState(0);
    const [reason, setReason] = useState('goodwill');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        if (amount <= 0) { setError('Amount must be greater than 0'); return; }
        setSubmitting(true);
        setError('');
        try {
            const r = await fetch(CANCEL_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json',
 'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}` },
                body: JSON.stringify({
                    action: 'adminIssueRefund',
                    customerUID: customer.UID,
                    amount,
                    reason,
                    actor: 'admin',
                }),
            });
            const data = await r.json();
            if (!r.ok) { setError(data.error || 'Refund failed'); return; }
            alert(`Refund issued: $${(data.refund?.amount ?? amount).toFixed(2)} (${data.refund?.status})`);
            onSuccess();
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
            <div className="bg-[#282828] border border-[#3c3836] rounded-2xl max-w-md w-full">
                <div className="px-5 py-3 border-b border-[#3c3836] flex items-center justify-between">
                    <h3 className="font-bold text-[#ebdbb2]">Issue Refund</h3>
                    <button onClick={onClose} className="text-[#928374] hover:text-[#ebdbb2]"><FaTimes /></button>
                </div>
                <div className="p-5 space-y-4">
                    <p className="text-sm text-[#bdae93]">
                        Refunds the customer's last paid invoice via Stripe. The customer will see the credit on their card/bank within 5-10 business days.
                    </p>
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#bdae93] mb-2">Amount (AUD)</label>
                        <input
                            type="number" step="0.01" min="0"
                            value={amount}
                            onChange={(e) => setAmount(Number(e.target.value))}
                            placeholder="e.g. 25.00"
                            className="w-full px-3 py-2 bg-[#1d2021] border border-[#3c3836] rounded text-[#ebdbb2] focus:outline-none focus:border-[#fabd2f]"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#bdae93] mb-2">Reason</label>
                        <select
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="w-full px-3 py-2 bg-[#1d2021] border border-[#3c3836] rounded text-[#ebdbb2]"
                        >
                            <option value="goodwill">Goodwill</option>
                            <option value="service_fault">Service fault</option>
                            <option value="billing_error">Billing error</option>
                            <option value="overcharge">Overcharge</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    {error && (
                        <div className="p-3 bg-[#fb4934]/20 border border-[#fb4934]/40 rounded text-sm text-[#fb4934]">{error}</div>
                    )}
                </div>
                <div className="px-5 py-4 border-t border-[#3c3836] flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="px-4 py-2 border border-[#3c3836] text-[#bdae93] rounded hover:bg-[#3c3836] text-sm"
                    >Cancel</button>
                    <button
                        onClick={submit}
                        disabled={submitting || amount <= 0}
                        className="px-4 py-2 bg-[#fabd2f] text-[#1d2021] rounded font-bold disabled:opacity-50 inline-flex items-center gap-2 text-sm"
                    >
                        {submitting ? <><FaSpinner className="animate-spin" /> Processing…</> : `Issue $${amount.toFixed(2)} refund`}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ───────── Manual Promo / Retention Modal ─────────
const ManualPromoModal = ({ customer, onClose, onSuccess }) => {
    const [offerType, setOfferType] = useState('extend_promo');
    const [durationMonths, setDurationMonths] = useState(3);
    const [amount, setAmount] = useState(0);
    const [newPlan, setNewPlan] = useState('Home Standard');
    const [creditAmount, setCreditAmount] = useState(0);
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const submit = async () => {
        setSubmitting(true);
        setError('');
        try {
            const offerDetails = { durationMonths, amount, newPlan, creditAmount, note };
            const r = await fetch(CANCEL_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json',
 'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}` },
                body: JSON.stringify({
                    action: 'adminApplyPromo',
                    customerUID: customer.UID,
                    offerType,
                    offerDetails,
                    actor: 'admin',
                }),
            });
            const data = await r.json();
            if (!r.ok) { setError(data.error || 'Failed to apply offer'); return; }
            alert('Offer applied successfully.');
            onSuccess();
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
            <div className="bg-[#282828] border border-[#3c3836] rounded-2xl max-w-md w-full">
                <div className="px-5 py-3 border-b border-[#3c3836] flex items-center justify-between">
                    <h3 className="font-bold text-[#ebdbb2]">Apply Promo / Offer</h3>
                    <button onClick={onClose} className="text-[#928374] hover:text-[#ebdbb2]"><FaTimes /></button>
                </div>
                <div className="p-5 space-y-3">
                    <p className="text-sm text-[#bdae93]">
                        Apply a retention or goodwill offer to this customer's account. Updates the pricing override immediately.
                    </p>
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#bdae93] mb-2">Offer type</label>
                        <select
                            value={offerType}
                            onChange={(e) => setOfferType(e.target.value)}
                            className="w-full px-3 py-2 bg-[#1d2021] border border-[#3c3836] rounded text-[#ebdbb2]"
                        >
                            <option value="extend_promo">Extend current promo</option>
                            <option value="one_free_month">One free month</option>
                            <option value="discount">Discount for N months</option>
                            <option value="plan_downgrade">Downgrade plan</option>
                            <option value="credit">Apply credit</option>
                        </select>
                    </div>
                    {(offerType === 'extend_promo' || offerType === 'discount') && (
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-[#bdae93] mb-2">Duration (months)</label>
                            <input
                                type="number" min="1" max="24"
                                value={durationMonths}
                                onChange={(e) => setDurationMonths(Number(e.target.value))}
                                className="w-full px-3 py-2 bg-[#1d2021] border border-[#3c3836] rounded text-[#ebdbb2]"
                            />
                        </div>
                    )}
                    {offerType === 'discount' && (
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-[#bdae93] mb-2">New monthly fee (AUD)</label>
                            <input
                                type="number" step="0.01" min="0"
                                value={amount}
                                onChange={(e) => setAmount(Number(e.target.value))}
                                className="w-full px-3 py-2 bg-[#1d2021] border border-[#3c3836] rounded text-[#ebdbb2]"
                            />
                        </div>
                    )}
                    {offerType === 'plan_downgrade' && (
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-[#bdae93] mb-2">Downgrade to</label>
                            <select
                                value={newPlan}
                                onChange={(e) => setNewPlan(e.target.value)}
                                className="w-full px-3 py-2 bg-[#1d2021] border border-[#3c3836] rounded text-[#ebdbb2]"
                            >
                                <option value="Home Basic">Home Basic — $59.99</option>
                                <option value="Home Standard">Home Standard — $79.99</option>
                                <option value="Home Fast">Home Fast — $89.99</option>
                            </select>
                        </div>
                    )}
                    {offerType === 'credit' && (
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-[#bdae93] mb-2">Credit amount (AUD)</label>
                            <input
                                type="number" step="0.01" min="0"
                                value={creditAmount}
                                onChange={(e) => setCreditAmount(Number(e.target.value))}
                                className="w-full px-3 py-2 bg-[#1d2021] border border-[#3c3836] rounded text-[#ebdbb2]"
                            />
                        </div>
                    )}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#bdae93] mb-2">Internal note</label>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 bg-[#1d2021] border border-[#3c3836] rounded text-[#ebdbb2]"
                        />
                    </div>
                    {error && (
                        <div className="p-3 bg-[#fb4934]/20 border border-[#fb4934]/40 rounded text-sm text-[#fb4934]">{error}</div>
                    )}
                </div>
                <div className="px-5 py-4 border-t border-[#3c3836] flex justify-end gap-2">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 border border-[#3c3836] text-[#bdae93] rounded hover:bg-[#3c3836] text-sm">Cancel</button>
                    <button onClick={submit} disabled={submitting}
                        className="px-4 py-2 bg-[#83a598] text-[#1d2021] rounded font-bold disabled:opacity-50 inline-flex items-center gap-2 text-sm">
                        {submitting ? <><FaSpinner className="animate-spin" /> Applying…</> : 'Apply offer'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CustomerDetailsModal;
